import { fetchClinicData } from '@/entities/clinic';
import {
  fetchElectrophysiologistEvent,
  fetchStudyInfoClinic,
  fetchStudyInfoClinicWebView,
  fetchStudyLogs,
  handleCancelStudy,
  STUDY_STATUS,
  STUDY_PENDING_COMMAND,
  checkBiotresRefCodeWarning,
  isC1cStudy,
} from '@/entities/study';
import { fetchRefreshShipmentTracking } from '@/entities/rma';
import { fetchUserSignature, useUserStore } from '@/entities/user';
import { fetchPhysicians, type ReferringPhysician } from '@/entities/physician';
import { useLoadingStore } from '@/shared/model';
import { EMITTER_CONSTANTS, LOADING_TYPES } from '@/shared/config';
import {
  generateToken,
  handleThrowErrorWebView,
  isWebViewCreateStudy,
  zeroPad,
  auth,
  emitter,
  showMessageError,
  showMessageInfo,
  getAdjustedStudyDayOffset,
  getStudyDuration,
  SocketIo,
  GETTEXT_ALL_STUDIES,
  handleFetchCountries,
  preventAppScroll,
} from '@/shared/lib';
import { useEmitter, useSetState, useUpdateEffect } from '@/shared/lib/customHooks';
import type {
  DeviceEventSocketMessage,
  DeviceStartedStudySocketMessage,
  InboxSocketMessage,
  MarkStatusReportSocketMessage,
  ReportUpdatedSocketMessage,
  StudyDeletedSocketMessage,
  StudyEventSocketMessage,
  StudyUpdatedSocketMessage,
} from '@/shared/lib';
import axios from 'axios';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { t } from 'ttag';
import type { AssignedDeviceStudy } from './studyDetailsTab/studyDetailsTab.types';
import type { CallBackUpdateData } from './studyDetailsTab/editStudyDetails/editStudyDetails.types';
import { fetchReport, handCheckSizeReports } from '../api';
import type { ClickedData, Report, Study, StudyManagementState } from './studyManagement.types';
import { checkHasReturnStatusTab } from '../lib/studyManagement';

/**
 * Drives the study-management page: loads one study, keeps it in sync with socket traffic and exposes the tab, drawer and ECG viewer actions its sections need. On mount it sets the document title, blocks app scroll and fetches the study, subscribes to the device, study, report, inbox and mark-status emitter channels, honours navigation state opening the verified or instant report tab, refetches when studyId changes, and forces the overview tab for aborted studies.
 *
 * @returns Study state together with the refs, derived flags and handlers consumed by the page.
 */
export const useStudyManagement = () => {
  const { studyId } = useParams();

  const location = useLocation();
  const locationState = location.state;

  const navigate = useNavigate();

  /**
   * The patient-visibility scope currently selected by the signed-in user. Read from the user store and used to decide whether a physician stays on a study that was reassigned to someone else.
   *
   * @returns Selected see-patients option from the user store.
   */
  const selectedTypeSeePatientsRedux = useUserStore((s) => s.selectedTypeSeePatients);

  /**
   * The global loading text setter selected from the loading store, used to show and clear the ECG viewer overlay. Subscribing to setLoadingText lets the hook drive the page-level loading indicator.
   *
   * @param loadingText - Loading label to display, omitted to clear the overlay.
   * @returns Nothing.
   */
  const loadingPageAction = useLoadingStore((s) => s.setLoadingText);

  const instantReportTabRef = useRef<{ handleClickBack: () => void } | null>(null);
  const hasOpenedDisalimerModal = useRef(false);
  const isWebView = useRef(isWebViewCreateStudy());
  const headerRef = useRef(null);
  const hasNavigatedFromInboxPage = useRef(false);
  const [commentBoxVisible, setCommentBoxVisible] = useState(true);

  /**
   * Picks which study-management tab should open first based on the navigation state carried into the page. Return-status navigation yields '7', a verified or view-report request yields '3', an instant-report request '6', an events request '4', and anything else falls back to the overview tab '1'.
   *
   * @returns Key of the tab to activate initially.
   */
  const generateDefaultActiveTab = () => {
    if (locationState?.shouldNavigateToReturnStatusTab) {
      return '7';
    }
    if (locationState?.shouldLoadVerifiedReport || locationState?.shouldShoulViewReportTab) {
      return '3';
    }
    if (locationState?.shouldViewInstantReportTab) {
      return '6';
    }
    if (locationState?.shouldNavigateToEventsTab) {
      return '4';
    }

    return '1';
  };

  /**
   * Builds the initial study-management state with the first tab chosen from the navigation state and every collection empty. The scroll, loaded, drawer and modal flags start false, studyDetails and both ECG viewer payloads start as empty objects, and hasLoginBanner is seeded from the auth service's login-by-banner flag.
   *
   * @returns Freshly initialised study-management state.
   */
  const getInitialState = (): StudyManagementState => ({
    activeTab: generateDefaultActiveTab(),
    isScroll: false,
    studyDetails: {},
    isLoadedData: false,
    isVisiblePatientInfoDrawer: false,
    ecgViewerData: {},
    inboxECGViewerData: {},
    countries: [],
    referringPhysicians: [],
    interprettingPhysicians: [],
    isOpenDisclaimerModal: false,
    hasReturnStatus: false,
    hasLoginBanner: auth.isShowLoginByBanner(),
    shouldLoadVerifiedReport: false,
    studyLogs: [],
  });

  const [state, setState] = useSetState<StudyManagementState>(getInitialState());

  /**
   * Whether an abort is currently in progress for the loaded study, memoised on the isAborting flag. Reads studyDetails.isAborting directly.
   *
   * @returns True while the study abort request is pending.
   */
  const isAbortingStudy = useMemo(
    () => state.studyDetails?.isAborting,
    [state.studyDetails?.isAborting],
  );
  /**
   * Whether the loaded study has been aborted, memoised on the study status. Compares studyDetails.status with the ABORTED study status.
   *
   * @returns True when the study status is ABORTED.
   */
  const isAbortedStudy = useMemo(
    () => state.studyDetails?.status === STUDY_STATUS.ABORTED,
    [state.studyDetails?.status],
  );
  /**
   * Whether the loaded study is archived, memoised on the study details object. Reads studyDetails.isArchived.
   *
   * @returns True when the study is archived.
   */
  const isArchivedStudy = useMemo(() => state.studyDetails?.isArchived, [state.studyDetails]);

  /**
   * Whether the loaded study comes from CardioOne, so its sections are read-only on this page. Reads only the study's external source provider, not the facility's `isCardioOne` flag.
   *
   * @returns True for a study whose external source provider is `c1c`.
   */
  const isC1cProviderStudy = isC1cStudy(state.studyDetails);

  /**
   * Whether the study is stopping while recording time still remains, memoised on the study details. Remaining minutes come from the study duration minus the elapsed time since start, and the result requires either a pending STOP_STUDY command or an isStopping study that is not yet DONE, ABORTED or COMPLETED.
   *
   * @returns True when a stop is pending and the study still has time left.
   */
  const hasStoppingStudyCommand = useMemo(() => {
    const { start, stop } = state.studyDetails;
    const duration = getStudyDuration(start, stop);
    const diff = dayjs().diff(start, 'minute', true);
    const minutesDiff = stop && diff > 0 ? diff : 0;
    const remainingMinutes = duration - minutesDiff;
    const isStopping =
      state.studyDetails?.isStopping &&
      !_.includes(
        [STUDY_STATUS.DONE, STUDY_STATUS.ABORTED, STUDY_STATUS.COMPLETED],
        state.studyDetails?.status,
      );
    return (
      (state.studyDetails?.pendingCommand?.command === STUDY_PENDING_COMMAND.STOP_STUDY ||
        isStopping) &&
      remainingMinutes > 0
    );
  }, [state.studyDetails]);

  /**
   * Sends the user back to the list they came from when leaving the study details page. Navigation state requesting the return-status tab routes to /rma, a previous visit from the inbox routes to /inbox, and otherwise the study list /studies is used.
   *
   * @returns Nothing.
   */
  const onClickBack = () => {
    if (locationState?.shouldNavigateToReturnStatusTab) {
      navigate('/rma');
    } else if (hasNavigatedFromInboxPage.current) {
      navigate('/inbox');
    } else {
      navigate('/studies');
    }
  };

  /**
   * Reloads the audit log entries of the study being viewed. The logs are fetched for the current studyId and merged into state, while a failure is only written to the console and never surfaced to the user.
   *
   * @returns Promise resolved once the logs are stored or the failure logged.
   */
  const handleFetchStudyLogs = async () => {
    try {
      const studyLogs = await fetchStudyLogs(studyId);
      setState({ studyLogs });
    } catch (error) {
      console.error('Failed to fetch study logs: ', error);
    }
  };

  /**
   * Loads everything the study-management page needs for one study and seeds its state. Study details come from the web-view or clinic query, followed by countries, study logs and both physician lists, plus a clinic physician's signature downloaded with a generated access token and inlined as base64; the day offset, return-status availability and Biotres disclaimer flag are computed and the device report room joined, while a missing or failed study warns the user and navigates back.
   *
   * @returns Promise resolved once the page state is populated or the failure handled.
   */
  const fetchData = async () => {
    try {
      const study = isWebView.current
        ? await fetchStudyInfoClinicWebView(studyId)
        : await fetchStudyInfoClinic(studyId);
      if (study) {
        const countriesList = await handleFetchCountries();
        const studyLogs = await fetchStudyLogs(studyId);

        const setStateObject = {
          isLoadedData: true,
          countries: countriesList,
          studyLogs,
        };

        if (!hasOpenedDisalimerModal.current) {
          hasOpenedDisalimerModal.current = true;
          if (checkBiotresRefCodeWarning(study as any)) {
            _.assign(setStateObject, { isOpenDisclaimerModal: true });
          }
        }

        const [referringPhysicians, interprettingPhysicians] = await Promise.all([
          fetchPhysicians({ facilityId: study.facility?.id }),
          fetchPhysicians({ facilityId: study.facility?.id, hasUsername: true }),
        ]);
        _.assign(setStateObject, { referringPhysicians, interprettingPhysicians });

        if (auth.isClinicPhysician()) {
          const userResult = await fetchUserSignature(auth.userId());
          const { data: userData } = userResult;
          const { user } = userData;
          if (user?.signature) {
            const accessToken = await generateToken();
            const getSignatureResponse = await axios.get(user.signature, {
              responseType: 'arraybuffer',
              headers: {
                'access-token': accessToken,
              },
            });
            if (!_.isEmpty(getSignatureResponse)) {
              if (getSignatureResponse.status === 200) {
                const buffer = Buffer.from(getSignatureResponse.data);
                const base64String = buffer.toString('base64');
                _.assign(study, { signatureBase64String: `data:image/png;base64,${base64String}` });
              } else {
                console.error('Failed to get signature');
              }
            }
          }
        }

        const adjustedStudyDayOffset = getAdjustedStudyDayOffset({
          startStudyDate: study.start ?? undefined,
          timezone: study.timezone ?? undefined,
          studyTimeOffset: study.studyTimeOffset ?? undefined,
        });

        // *: For testing
        // study.unreviewedReport = null;

        // *: For testing (CP-2.25.0)
        // study.status = STUDY_STATUS.ONGOING;

        // *: For testing (CP-2.25.1)
        // study.sentReferenceCodeAt = null;

        // *: For testing (CP-2.29.0)
        // study.isAborting = true;
        // study.status = STUDY_STATUS.ABORTED;

        _.assign(setStateObject, {
          studyDetails: study,
          adjustedStudyDayOffset,
          hasReturnStatus: checkHasReturnStatusTab(study as any),
        });

        setState(setStateObject as any);
        // *: Join device room
        if (study.device?.deviceId) {
          const deviceRoomId = `report-updated-${study.device?.deviceId}`;
          (SocketIo as any).emitRoom(deviceRoomId);
        }
      } else {
        if (isWebView.current) {
          handleThrowErrorWebView(
            t`You cannot access the study details since this study has been deleted.`,
          );
          return;
        }
        showMessageInfo({
          content: t`You cannot access the study details since this study has been deleted`,
        });
        const linkGoBack = locationState?.linkGoBack || '/studies';
        navigate(linkGoBack, { replace: true });
        setState({ isLoadedData: true });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error(t`Failed to fetch study detail: `, errorMessage);
      if (isWebView.current) {
        handleThrowErrorWebView(
          t`You cannot access the study details since this study has been deleted.`,
        );
        return;
      }
      if (locationState?.linkGoBack) {
        showMessageError({
          content: t`You cannot access the study details since this study has been deleted`,
        });
        const linkGoBack = locationState?.linkGoBack || '/studies';
        navigate(linkGoBack, { replace: true });
        setState({ isLoadedData: true });
        return;
      }
      if (errorMessage === 'Error: Study not found') {
        showMessageError({ content: t`Study was deleted` });
        navigate('/studies', { replace: true });
        return;
      }
      showMessageError({ content: errorMessage });
    }
  };

  // *: For testing (CP-2.25.0)
  // const isInitial = useRef(true);
  // useUpdateEffect(() => {
  //   if (!_.isEmpty(state.studyDetails) && isInitial.current) {
  //     isInitial.current = false;
  //     setTimeout(() => {
  //       const clonedStudyDetail = _.cloneDeep(state.studyDetails);
  //       clonedStudyDetail.status = STUDY_STATUS.DONE;
  //       setState({ studyDetails: clonedStudyDetail });
  //     }, 20000);
  //   }
  // }, [state.studyDetails]);

  /**
   * Switches the study-management page to another tab. Any open instant-report detail view is dismissed first through the tab ref's handleClickBack, then the tab key is stored only when it differs from the active one.
   *
   * @param tab - Key of the tab to activate.
   * @returns Nothing.
   */
  const navigateTab = (tab: string) => {
    if (instantReportTabRef.current) {
      instantReportTabRef.current.handleClickBack();
    }
    if (tab !== state.activeTab) {
      setState({ activeTab: tab });
    }
  };

  /**
   * Shows or hides the patient information drawer of the study page. The current isVisiblePatientInfoDrawer flag is inverted in state.
   *
   * @returns Nothing.
   */
  const toggleDisplayPatientInfoDrawer = () => {
    setState({ isVisiblePatientInfoDrawer: !state.isVisiblePatientInfoDrawer });
  };

  /**
   * Opens or closes the ECG viewer for a single electrophysiologist event. With an event id the page loading indicator is raised and the fetched event flattened into ecgViewerData (friendly id, type, validity, study start, trigger time, timezones, facility country, post-event time, diagnostic lead, report data and heart-rate info); errors are logged and toasted, and an empty id clears the viewer data.
   *
   * @param eventId - Event to display, or a falsy value to close the viewer.
   * @returns Promise resolved once the viewer data is stored or cleared.
   */
  const toggleECGViewer = async (eventId: string) => {
    if (eventId) {
      loadingPageAction(LOADING_TYPES.VIEW_ECG);
      const ecgViewerData = {};
      try {
        const electrophysiologistEvent = await fetchElectrophysiologistEvent(eventId);
        _.assign(ecgViewerData, {
          eventFid: electrophysiologistEvent?.friendlyId,
          eventType: electrophysiologistEvent?.type,
          isValid: electrophysiologistEvent?.isValid,
          startStudyDate: electrophysiologistEvent?.study?.start,
          triggerTime: electrophysiologistEvent?.start,
          timezone: electrophysiologistEvent?.study?.timezone,
          studyTimeOffset: electrophysiologistEvent?.study?.studyTimeOffset,
          facilityCountry: electrophysiologistEvent?.study?.facility?.contact?.country,
          facilityTimezone: electrophysiologistEvent?.study?.facility?.timezone,
          postEventTime: electrophysiologistEvent?.eventSetting?.postEventTime,
          diagnosticLead: electrophysiologistEvent?.eventSetting?.diagnosticLead,
          reportData: electrophysiologistEvent?.reportData,
          hrInfo: electrophysiologistEvent?.hrInfo,
          study: electrophysiologistEvent?.study,
        });
        setState({ ecgViewerData });
      } catch (error) {
        console.error('Failed to fetch electrophysiologist event', error);
        showMessageError({ content: error instanceof Error ? error.message : String(error) });
      } finally {
        loadingPageAction();
      }
    } else {
      setState({ ecgViewerData: {} });
    }
  };

  /**
   * Opens or closes the ECG viewer for an inbox report row. With clicked data the loading indicator is raised and inboxECGViewerData seeded from the row (study friendly id, patient and interpreting physician names, event time, timezones and facility country) before the clinic data for that study and event pair is fetched and merged; errors are logged and toasted, and no data clears the viewer.
   *
   * @param clickedData - Inbox row that was clicked, or a falsy value to close the viewer.
   * @returns Promise resolved once the viewer data is stored or cleared.
   */
  const toggleInboxECGViewer = async (clickedData: ClickedData) => {
    if (clickedData) {
      loadingPageAction(LOADING_TYPES.VIEW_ECG);
      const inboxECGViewerData = {
        studyFid: clickedData.studyFid,
        patientName: clickedData.patientName,
        interprettingPhysicianName: clickedData.interprettingPhysicianName,
        startStudyDate: clickedData.study?.start,
        eventTime: clickedData.eventTimeUTC,
        timezone: clickedData.timezone,
        studyTimeOffset: clickedData.study?.studyTimeOffset,
        facilityCountry: clickedData.study?.facility?.contact?.country,
        facilityTimezone: clickedData.study?.facility?.timezone,
      };
      try {
        const clinicData = await fetchClinicData({
          studyId: clickedData.study.id,
          eventId: clickedData.event.id,
        });
        _.assign(inboxECGViewerData, { clinicData });
        setState({ inboxECGViewerData });
      } catch (error) {
        console.error('Failed to fetch clinic data: ', error);
        showMessageError({ content: error instanceof Error ? error.message : String(error) });
      } finally {
        loadingPageAction();
      }
    } else {
      setState({ inboxECGViewerData: {} });
    }
  };

  /**
   * Asks the backend to re-check the generated report sizes of an archived study. A response without isSuccess raises an error toast telling the user to submit a support request, while a thrown error is only logged.
   *
   * @returns Promise resolved once the check has been answered or the error logged.
   */
  const handleCheckReportSize = async () => {
    try {
      const result = await handCheckSizeReports(studyId);
      if (!result?.isSuccess) {
        showMessageError({
          content:
            'The report could not be regenerated automatically. Please submit a support request for assistance with updating the study information.',
        });
      }
    } catch (error) {
      console.log('Failed to check report size', error);
    }
  };

  /**
   * Reloads the whole study after an edit that touches more than a few fields. isLoadedData is dropped so the loader shows, the study fetch re-runs, and an archived study also triggers the report-size check when requested.
   *
   * @param couldCheckReportSize - Whether the report-size check should run for an archived study.
   * @returns Nothing.
   */
  const updateStudyDetail = (couldCheckReportSize?: boolean) => {
    setState({ isLoadedData: false });
    fetchData();
    if (couldCheckReportSize && isArchivedStudy) {
      handleCheckReportSize();
    }
  };

  /**
   * Merges fields edited in the study information forms into the open study without a full refetch. Non-empty referringPhysician, interprettingPhysician, diagnosis and signatureBase64String are assigned onto a clone of studyDetails, the study logs are reloaded, and an archived study additionally triggers the report-size check when the caller asks for it.
   *
   * @param data - Edited study fields to merge into the loaded study.
   * @param couldCheckReportSize - Whether the report-size check should run for an archived study.
   * @returns Nothing.
   */
  const updateStudyData = (data: CallBackUpdateData, couldCheckReportSize?: boolean) => {
    const clonedStudyDetail = _.cloneDeep(state.studyDetails);
    const { referringPhysician, interprettingPhysician, diagnosis, signatureBase64String } =
      data || {};
    if (!_.isEmpty(referringPhysician)) {
      _.assign(clonedStudyDetail, { info: { ...clonedStudyDetail.info, referringPhysician } });
    }
    if (!_.isEmpty(interprettingPhysician)) {
      _.assign(clonedStudyDetail, { info: { ...clonedStudyDetail.info, interprettingPhysician } });
    }
    if (!_.isEmpty(diagnosis)) {
      _.assign(clonedStudyDetail, {
        info: {
          ...clonedStudyDetail.info,
          diagnosis: {
            ...clonedStudyDetail.info.diagnosis,
            ...diagnosis,
          },
        },
      });
    }
    if (!_.isEmpty(signatureBase64String)) {
      _.assign(clonedStudyDetail, {
        signatureBase64String,
      });
    }
    setState({ studyDetails: clonedStudyDetail });
    // fetch updated study logs cuz this update dont need to fecth full study detail
    handleFetchStudyLogs();
    if (couldCheckReportSize && isArchivedStudy) {
      handleCheckReportSize();
    }
  };

  /**
   * Replaces the report collection of the open study with a caller-supplied list. studyDetails is spread into a new object carrying the new reports so the report sections re-render.
   *
   * @param newReports - Reports that should replace the study's current list.
   * @returns Nothing.
   */
  const handleUpdateReports = (newReports: Report[]) => {
    setState({
      studyDetails: {
        ...state.studyDetails,
        reports: newReports,
      },
    });
  };

  /**
   * Dismisses the Biotres reference-code disclaimer modal once the user acknowledges it. Only isOpenDisclaimerModal is set back to false.
   *
   * @returns Nothing.
   */
  const onClickOkayDisclaimerModal = () => {
    setState({ isOpenDisclaimerModal: false });
  };

  /**
   * Folds the outcome of a patient-return status change back into the open study. The supplied study fields are spread over the existing studyDetails.
   *
   * @param study - Study fields returned by the return-status update.
   * @returns Nothing.
   */
  const updateReturnStatus = (study: Study) => {
    setState({
      studyDetails: {
        ...state.studyDetails,
        ...study,
      },
    });
  };

  /**
   * Folds the outcome of sending a study reference code back into the open study. The supplied study fields are spread over the existing studyDetails.
   *
   * @param study - Study fields returned by the send-reference-code call.
   * @returns Nothing.
   */
  const updateSentReferenceCodeStatus = (study: Study) => {
    setState({
      studyDetails: {
        ...state.studyDetails,
        ...study,
      },
    });
  };

  /**
   * Aborts the open study in the customer environment and reports the outcome back to the caller. isAborting is raised on a clone of studyDetails before the cancel-study call runs; success invokes the callback with true, and a failure logs, shows an error toast, clears isAborting and calls back with false.
   *
   * @param callbackAbortStudy - Callback receiving whether the abort succeeded.
   * @returns Promise resolved after the abort attempt has been reported.
   */
  const handleAbortStudy = async (callbackAbortStudy: (isSuccess: boolean) => void) => {
    const clonedStudyDetail = _.cloneDeep(state.studyDetails);
    try {
      clonedStudyDetail.isAborting = true;
      setState({ studyDetails: clonedStudyDetail });

      // For customer environment
      await handleCancelStudy(state.studyDetails.id);
      callbackAbortStudy(true);
      // *: For testing (CP-2.29.0)
      // setTimeout(() => {
      //   clonedStudyDetail.isAborting = false;
      //   setState({ studyDetails: clonedStudyDetail });
      //   callbackAbortStudy(true);
      // }, 3000);
    } catch (error) {
      console.error('Failed to abort study: ', error);
      showMessageError({ content: error instanceof Error ? error.message : String(error) });
      clonedStudyDetail.isAborting = false;
      setState({ studyDetails: clonedStudyDetail });
      callbackAbortStudy(false);
    }
  };

  /**
   * Opens the report tab so the verified report is shown. The active tab is set to '3' and shouldLoadVerifiedReport raised so the tab loads the verified document.
   *
   * @returns Nothing.
   */
  const openVerifiedReportTab = () => {
    setState({
      activeTab: '3',
      shouldLoadVerifiedReport: true,
    });
  };

  /**
   * Handles the user's click on View verified report. A CardioOne study raises the CardioOne report notice and keeps the current tab; any other study opens the report tab.
   *
   * @returns Nothing.
   */
  const handleViewVerifiedReportClick = () => {
    if (isC1cStudy(state.studyDetails)) {
      emitter.emit(EMITTER_CONSTANTS.SHOW_POPUP_C1C_REPORT);
      return;
    }
    openVerifiedReportTab();
  };

  /**
   * Clears the one-shot flag that asks the report tab to load the verified report. shouldLoadVerifiedReport is set back to false so a later visit to the tab does not reload it.
   *
   * @returns Nothing.
   */
  const resetShouldLoadVerifiedReport = () => {
    setState({ shouldLoadVerifiedReport: false });
  };

  /**
   * Refreshes the study after a device has been assigned to it. The in-place merge of the assigned device and its study setting is left commented out, so the call simply delegates to the full study-detail reload.
   *
   * @param _assignedDeviceStudy - Study returned by the device assignment, currently unused.
   * @returns Nothing.
   */
  const handleUpdateStudyAfterAssigningDevice = (_assignedDeviceStudy: AssignedDeviceStudy) => {
    // TODO: Update study details after assigning device
    // const clonedStudyDetails = _.cloneDeep(state.studyDetails);
    // _.assign(clonedStudyDetails, {
    //   device: assignedDeviceStudy.device,
    //   isDeviceAssignmentPending: assignedDeviceStudy.isDeviceAssignmentPending,
    //   info: {
    //     ...clonedStudyDetails.info,
    //     studySetting: assignedDeviceStudy.info.studySetting,
    //   },
    // });
    // setState({ studyDetails: clonedStudyDetails });

    updateStudyDetail();
  };

  /**
   * Adds a newly created referring physician to the top of the selectable physician list. The current referringPhysicians array is cloned, the user unshifted onto it, and the result written back to state.
   *
   * @param user - Referring physician that was just created.
   * @returns Nothing.
   */
  const handleUpdateReferringPhysicians = (user: ReferringPhysician) => {
    const clonedReferringPhysicians = _.cloneDeep(state.referringPhysicians);
    clonedReferringPhysicians.unshift(user);
    setState({ referringPhysicians: clonedReferringPhysicians });
  };

  /**
   * Refreshes the courier tracking of one shipment shown on the study and writes it back into state. Outbound and inbound Biodirect types update the latest label of the sender or recipient information, the return-status tab type locates the return label by transaction id and assigns the refreshed tracking at the given index, errors raise a toast, and the optional callback always runs.
   *
   * @param type - Which shipment block is being refreshed: OutboundBiodirect, InboundBiodirect or ReturnStatusTab.
   * @param transactionId - Shipment transaction whose tracking is refreshed.
   * @param ticketId - Ticket the shipment belongs to.
   * @param index - Position of the return label inside the returnLabels list.
   * @param callback - Optional callback run after the refresh, success or failure.
   * @returns Promise resolved once tracking is stored and the callback invoked.
   */
  const handleRefreshTracking = async ({
    type,
    transactionId,
    ticketId,
    index,
    callback,
  }: {
    type: string;
    transactionId: string;
    ticketId: string;
    index: number;
    callback?: () => void;
  }) => {
    try {
      const tracking = await fetchRefreshShipmentTracking({
        transactionId,
        ticketId,
      });
      if (['OutboundBiodirect', 'InboundBiodirect'].includes(type)) {
        const clonedStudyDetails = _.cloneDeep(state.studyDetails);
        const latestLabel =
          type === 'OutboundBiodirect'
            ? clonedStudyDetails?.biofluxDirect?.senderInformation?.latestLabel
            : clonedStudyDetails?.biofluxDirect?.recipientInformation?.latestLabel;
        if (latestLabel) {
          latestLabel.tracking = tracking;
        }
        setState({ studyDetails: clonedStudyDetails });
        return;
      }

      if (type === 'ReturnStatusTab') {
        const clonedStudyDetails = _.cloneDeep(state.studyDetails);
        const labels = clonedStudyDetails?.patientReturn?.returnLabels;
        /**
         * The patient return label whose shipment transaction id matches the transaction being refreshed. Selected with a lodash lookup over the study's patientReturn.returnLabels collection.
         *
         * @returns Matching return label, or undefined when no label uses that transaction.
         */
        const foundLabel = _.find(labels, (x) => x.transaction.id === transactionId);
        if (foundLabel) {
          foundLabel.tracking = tracking;
        }
        _.assign(labels?.[index], { ...foundLabel });
        setState({ studyDetails: clonedStudyDetails });
      }
    } catch (error) {
      console.error('Failed to refresh tracking: ', error);
      showMessageError({ content: error instanceof Error ? error.message : String(error) });
    } finally {
      if (_.isFunction(callback)) {
        callback();
      }
    }
  };

  /**
   * Applies a device status change pushed over the socket to the study currently displayed. The state updater compares the loaded study's device id against the message's and only then clones studyDetails to write the new device status, otherwise leaving state untouched.
   *
   * @param msg - Socket payload carrying the device id and its new status.
   * @returns Nothing.
   */
  const handleDeviceEventSocket = (msg: DeviceEventSocketMessage) => {
    setState((prevState) => {
      if (prevState.studyDetails.device?.deviceId === msg?.deviceId) {
        const clonedStudyDetails = _.cloneDeep(prevState.studyDetails);
        clonedStudyDetails.device.status = msg.status;
        return { studyDetails: clonedStudyDetails };
      }

      return {};
    });
  };

  /**
   * Applies a study-updated socket message to the open study and enforces interpreting-physician access. A clinic physician who loses the assignment is redirected to /studies or thrown out of the create-study web view; otherwise a clone takes rma, lowCellSignal, studyNote, studyType, pendingCommand, status, aborting, artifact and flag fields, refreshes the study's own entry in linkedStudies, resolves the follow-on study, and closes the disclaimer modal on done or aborted.
   *
   * @param msg - Socket payload with the updated study fields.
   * @returns Nothing.
   */
  const handleStudyUpdatedSocket = (msg: StudyUpdatedSocketMessage) => {
    setState((prevState) => {
      const setStateObject = {};

      if (prevState.studyDetails.id === msg.id) {
        if (auth.isClinicPhysician()) {
          if (msg?.info?.interprettingPhysician) {
            if (selectedTypeSeePatientsRedux.value !== GETTEXT_ALL_STUDIES) {
              navigate('/studies', { replace: true });
              return {};
            }

            //* Kick user out of study detail if user is physician and study has been assigned to other interpretting physician
            if (selectedTypeSeePatientsRedux.value === GETTEXT_ALL_STUDIES) {
              if (
                auth.isClinicPhysician() &&
                prevState.studyDetails.info.interprettingPhysician.id !==
                  msg.info.interprettingPhysician
              ) {
                if (isWebView.current) {
                  handleThrowErrorWebView(
                    t`You are no longer the interpreting physician of this study. That's why you cannot view and edit this study anymore`,
                    zeroPad(prevState.studyDetails?.friendlyId),
                  );
                  return {};
                }
              }
            }
          }
        }

        const clonedStudyDetails = _.cloneDeep(prevState.studyDetails);
        const clonedLinkedStudies = _.cloneDeep(clonedStudyDetails.linkedStudies);
        /**
         * The entry of the study's cloned linkedStudies list that represents the study itself. Located with a lodash lookup comparing each linked study id to the cloned study details id.
         *
         * @returns Matching linked study, or undefined when the study is not self-listed.
         */
        const updateLinkedStudy = _.find(
          clonedLinkedStudies,
          (x) => x.id === clonedStudyDetails.id,
        );
        if (updateLinkedStudy) {
          updateLinkedStudy.studyType = !_.isEmpty(msg.pendingCommand)
            ? msg.pendingCommand?.info?.studySetting?.studyType
              ? msg.pendingCommand?.info?.studySetting?.studyType
              : msg.studyType
                ? msg.studyType
                : clonedStudyDetails.studyType
            : msg.studyType
              ? msg.studyType
              : clonedStudyDetails.studyType;
        }

        clonedStudyDetails.linkedStudies = clonedLinkedStudies;
        clonedStudyDetails.rma = msg.rma !== undefined ? msg.rma : clonedStudyDetails.rma;
        clonedStudyDetails.lowCellSignal =
          msg.lowCellSignal !== undefined ? msg.lowCellSignal : clonedStudyDetails.lowCellSignal;
        clonedStudyDetails.studyNote =
          msg.studyNote !== undefined ? msg.studyNote : clonedStudyDetails.studyNote;
        clonedStudyDetails.studyType =
          msg.studyType !== undefined ? msg.studyType : clonedStudyDetails.studyType;
        clonedStudyDetails.pendingCommand =
          msg.pendingCommand !== undefined ? msg.pendingCommand : clonedStudyDetails.pendingCommand;
        clonedStudyDetails.status =
          msg.status !== undefined ? msg.status : clonedStudyDetails.status;
        if (msg.isAborting !== undefined) {
          clonedStudyDetails.isAborting = msg.isAborting;
        } else {
          clonedStudyDetails.isAborting =
            msg.status === STUDY_STATUS.ABORTED ? false : clonedStudyDetails.isAborting;
        }

        if (msg.artifact) {
          _.assign(clonedStudyDetails.artifact, { resolvedAt: msg.artifact.resolvedAt });
        }

        if (msg.flaggedStudy) {
          clonedStudyDetails.flaggedStudy = msg.flaggedStudy;
        }
        if (msg.lastAddressedTime) {
          clonedStudyDetails.lastAddressedTime = msg.lastAddressedTime;
        }
        if (!_.isUndefined(msg.flaggedStudySkip)) {
          clonedStudyDetails.flaggedStudySkip = msg.flaggedStudySkip;
        }

        clonedStudyDetails.patientFirstSyncDeviceAt =
          msg.patientFirstSyncDeviceAt !== undefined
            ? msg.patientFirstSyncDeviceAt
            : clonedStudyDetails.patientFirstSyncDeviceAt;
        clonedStudyDetails.patientPaired =
          msg.patientPaired !== undefined ? msg.patientPaired : clonedStudyDetails.patientPaired;

        const listNotActiveStatus = [STUDY_STATUS.DONE, STUDY_STATUS.ABORTED];
        if (state.isOpenDisclaimerModal) {
          if (msg.patientFirstSyncDeviceAt || _.includes(listNotActiveStatus, msg.status)) {
            _.assign(setStateObject, { isOpenDisclaimerModal: false });
          }
        }

        const { followOnStudy: followOnStudyTemp } = msg.pendingCommand?.info?.studySetting || {};
        if (!_.isNil(followOnStudyTemp)) {
          _.assign(clonedStudyDetails.info, {
            followOnStudy: !_.isEmpty(followOnStudyTemp) ? followOnStudyTemp : null,
          });
        } else if (!_.isNil(msg.followOnStudy)) {
          _.assign(clonedStudyDetails.info, {
            followOnStudy: !_.isEmpty(msg.followOnStudy) ? msg.followOnStudy : null,
          });
        }

        _.assign(setStateObject, { studyDetails: clonedStudyDetails });
      }

      return setStateObject;
    });
  };

  /**
   * Merges a study lifecycle socket event into the open study's details. Defined message fields overwrite start, stop, studyType, status, pendingCommand and reportDueDate on a clone, initDuration prefers the message studySetting duration, isStopping and studySetting are merged when present, a pending follow-on study is written into info, and a done or aborted status closes the disclaimer modal.
   *
   * @param msg - Socket payload describing the study lifecycle change.
   * @returns Nothing.
   */
  const handleStudyEventSocket = (msg: StudyEventSocketMessage) => {
    setState((prevState) => {
      const setStateObject = {};

      if (prevState.studyDetails.id === msg.studyId) {
        const clonedStudyDetails = _.cloneDeep(prevState.studyDetails);

        clonedStudyDetails.start = msg.start !== undefined ? msg.start : clonedStudyDetails.start;
        clonedStudyDetails.stop = msg.stop !== undefined ? msg.stop : clonedStudyDetails.stop;
        clonedStudyDetails.studyType =
          msg.studyType !== undefined ? msg.studyType : clonedStudyDetails.studyType;
        clonedStudyDetails.status =
          msg.status !== undefined ? msg.status : clonedStudyDetails.status;
        clonedStudyDetails.initDuration = msg.studySetting
          ? msg.studySetting.duration
          : msg.initDuration !== undefined
            ? msg.initDuration
            : clonedStudyDetails.studySetting?.duration || clonedStudyDetails.initDuration;
        clonedStudyDetails.pendingCommand = msg.pendingCommand ? msg.pendingCommand : undefined;
        clonedStudyDetails.reportDueDate =
          msg.reportDueDate !== undefined ? msg.reportDueDate : clonedStudyDetails.reportDueDate;

        if (!_.isNil(msg.isStopping)) {
          clonedStudyDetails.isStopping = msg.isStopping;
        }

        _.assign(clonedStudyDetails.studySetting, msg.studySetting);

        const { followOnStudy: followOnStudyTemp } = msg.pendingCommand?.info?.studySetting || {};
        if (!_.isNil(followOnStudyTemp)) {
          _.assign(clonedStudyDetails.info, {
            followOnStudy: !_.isEmpty(followOnStudyTemp) ? followOnStudyTemp : null,
          });
        }

        _.assign(setStateObject, { studyDetails: clonedStudyDetails });

        const listNotActiveStatus = [STUDY_STATUS.DONE, STUDY_STATUS.ABORTED];
        if (state.isOpenDisclaimerModal) {
          if (msg.patientFirstSyncDeviceAt || _.includes(listNotActiveStatus, msg.status)) {
            _.assign(setStateObject, { isOpenDisclaimerModal: false });
          }
        }
      }

      return setStateObject;
    });
  };

  /**
   * Reacts to a batch of study-deletion socket messages that may include the open study. When one of them matches, a message naming the zero-padded friendly id is thrown to the create-study web view or shown as an info toast before the route is replaced with /studies.
   *
   * @param msg - Socket payload listing the deleted studies.
   * @returns Nothing.
   */
  const handleStudyDeletedSocket = (msg: StudyDeletedSocketMessage[]) => {
    /**
     * The entry of the study-deleted socket payload that refers to the currently opened study. Found by matching each message studyId against the loaded study id.
     *
     * @returns Matching deletion message, or undefined when another study was deleted.
     */
    const matchDeletedStudy = _.find(msg, (x) => x.studyId === state.studyDetails.id);
    if (matchDeletedStudy) {
      const errorMessage = `${t`Study`} ${zeroPad(matchDeletedStudy.studyFid)} ${t`was deleted`}`;
      if (isWebView.current) {
        handleThrowErrorWebView(errorMessage, zeroPad(state.studyDetails?.friendlyId));
        return;
      }

      showMessageInfo({ content: errorMessage });
      navigate('/studies', { replace: true });
    }
  };

  /**
   * Pulls a report the page does not know about yet into the open study. The fetched report is pushed onto a clone of studyDetails.reports and stored, while a failure is logged and surfaced through an error toast.
   *
   * @param id - Identifier of the report to load.
   * @returns Promise resolved once the report is stored or the error reported.
   */
  const fetchNewReport = async (id: string) => {
    try {
      const clonedStudyDetails = _.cloneDeep(state.studyDetails);
      const report = await fetchReport(id);
      clonedStudyDetails.reports.push(report);
      setState({ studyDetails: clonedStudyDetails });
    } catch (error) {
      console.error('Failed to fetch new report: ', error);
      showMessageError({ content: error instanceof Error ? error.message : String(error) });
    }
  };

  /**
   * Applies a report-updated socket message to the open study, matched by its friendly id. An unknown report id triggers a fetch of that report, while a known one receives the new status and technicianComments before studyDetails is re-cloned into state.
   *
   * @param msg - Socket payload with the study friendly id, report id, status and technician comments.
   * @returns Nothing.
   */
  const handleReportUpdatedSocket = (msg: ReportUpdatedSocketMessage) => {
    if (state.studyDetails.friendlyId === msg.studyFid) {
      const { studyDetails } = state;
      if (!msg.id) {
        return;
      }
      /**
       * The report of the loaded study whose id matches the incoming report-updated message. Resolved by a lodash lookup over studyDetails.reports and undefined when the report has not been loaded yet.
       *
       * @returns Matching report, or undefined when the report is unknown to the page.
       */
      const ele = _.find(studyDetails.reports, (x) => x.id === msg.id);
      if (!ele) {
        fetchNewReport(msg.id);
      } else {
        ele.status = msg.status;
        ele.technicianComments = msg.technicianComments
          ? msg.technicianComments
          : ele.technicianComments;
        setState({ studyDetails: _.cloneDeep(studyDetails) });
      }
    }
  };

  /**
   * Updates the inbox metadata of one report of the open study from an inbox socket message. The matching report takes the new statusReport plus lastStatusReportAt, isNewFormat, aiFormatVersion and physicianInterpretation where present; a recalled message clears the inbox date and cached pdfArrayBuffer instead of storing the inbox payload, and studyDetails is re-cloned into state.
   *
   * @param msg - Socket payload identifying the report and its new inbox status fields.
   * @returns Nothing.
   */
  const handleInboxSocket = (msg: InboxSocketMessage) => {
    /**
     * The report of the loaded study whose id matches the incoming inbox message. Resolved by a lodash lookup over studyDetails.reports and undefined when no report matches.
     *
     * @returns Matching report, or undefined when the study has no such report.
     */
    const ele = _.find(state.studyDetails.reports, (x) => x.id === msg.id);
    if (ele) {
      ele.statusReport = msg.statusReport;
      ele.lastStatusReportAt = !_.isNil(msg.lastStatusReportAt)
        ? msg.lastStatusReportAt
        : ele.lastStatusReportAt;
      ele.isNewFormat = !_.isNil(msg.isNewFormat) ? msg.isNewFormat : ele.isNewFormat;
      ele.aiFormatVersion = !_.isNil(msg.aiFormatVersion)
        ? msg.aiFormatVersion
        : ele.aiFormatVersion;
      ele.physicianInterpretation = !_.isNil(msg.physicianInterpretation)
        ? msg.physicianInterpretation
        : ele.physicianInterpretation;
      if (msg.recalled) {
        ele.inbox = _.cloneDeep(ele.inbox);
        ele.inbox.date = null;
        ele.pdfArrayBuffer = null;
      } else {
        ele.inbox = msg.inbox;
      }
      setState({ studyDetails: _.cloneDeep(state.studyDetails) });
    }
  };

  /**
   * Applies a report status-marking socket message to the matching report of the open study. The message is assigned wholesale onto the located report and studyDetails is re-cloned into state, while an unknown report id leaves state unchanged.
   *
   * @param msg - Socket payload with the report id and the marked status fields.
   * @returns Nothing.
   */
  const handleMarkStatusReportSocket = (msg: MarkStatusReportSocketMessage) => {
    const { studyDetails } = state;
    /**
     * The report of the loaded study whose id matches the incoming mark-status message. Resolved by a lodash lookup over studyDetails.reports and undefined when the report is not loaded.
     *
     * @returns Matching report, or undefined when the study has no such report.
     */
    const ele = _.find(studyDetails.reports, (x) => x.id === msg.id);
    if (ele) {
      _.assign(ele, msg);
      setState({ studyDetails: _.cloneDeep(studyDetails) });
    }
  };

  /**
   * Reflects a device-started-study socket message on the open study. When the message studyId matches the loaded study, a clone of studyDetails receives the message status and referenceCode wherever those fields are defined, keeping the existing values otherwise.
   *
   * @param msg - Socket payload with the started study's id, status and reference code.
   * @returns Nothing.
   */
  const handleDeviceStartedStudySocket = (msg: DeviceStartedStudySocketMessage) => {
    if (state.studyDetails.id === msg.studyId) {
      const clonedStudyDetail = _.cloneDeep(state.studyDetails);
      clonedStudyDetail.status = msg.status !== undefined ? msg.status : clonedStudyDetail.status;
      clonedStudyDetail.referenceCode =
        msg.referenceCode !== undefined ? msg.referenceCode : clonedStudyDetail.referenceCode;
      setState({ studyDetails: clonedStudyDetail });
    }
  };

  /**
   * Records whether the login banner is showing above the study management page. Stores the
   * isShowLoginByBanner flag as hasLoginBanner in state.
   *
   * @param props - Banner change carrying isShowLoginByBanner.
   * @returns Nothing.
   */
  const handleLoginBannerDisplayChange = ({
    isShowLoginByBanner,
  }: {
    isShowLoginByBanner: boolean;
  }) => {
    setState({ hasLoginBanner: isShowLoginByBanner });
  };

  useEffect(() => {
    if (isAbortedStudy || isAbortingStudy) {
      const topContainer = document.getElementsByClassName('top-container');
      if (topContainer?.length) {
        topContainer[0].classList.remove('hidden');
      }
      setState({ activeTab: '1' });
    }
  }, [isAbortedStudy, isAbortingStudy]);

  useEffect(() => {
    /**
     * Tracks whether the window has scrolled away from the top so the sticky page header can react. isScroll is set false at scroll position zero and true otherwise, and written only when the flag actually changes.
     *
     * @returns Nothing.
     */
    const handleScroll = () => {
      if (window.scrollY === 0 && state.isScroll) {
        setState({ isScroll: false });
      } else if (window.scrollY !== 0 && !state.isScroll) {
        setState({ isScroll: true });
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [state.isScroll]);

  useEffect(() => {
    document.title = t`Biocare Diagnostics - Study Management`;

    window.scroll({ top: 0, left: 0, behavior: 'smooth' });

    preventAppScroll(true);

    fetchData();
  }, []);

  useEffect(() => {
    const deviceStatusListener = emitter.addListener(
      EMITTER_CONSTANTS.DEVICE_EVENT,
      handleDeviceEventSocket,
    );
    const studyUpdatedListener = emitter.addListener(
      EMITTER_CONSTANTS.STUDY_UPDATED_EVENT,
      handleStudyUpdatedSocket,
    );
    /**
     * The emitter subscription that forwards study lifecycle socket events to the study event handler. Registered on the STUDY_EVENT channel and removed when the effect cleans up.
     *
     * @returns Listener handle exposing remove().
     */
    const studyEventListener = emitter.addListener(
      EMITTER_CONSTANTS.STUDY_EVENT,
      handleStudyEventSocket,
    );
    /**
     * The emitter subscription that forwards study-deleted socket events to the deletion handler. Registered on the STUDY_DELETED channel and removed when the effect cleans up.
     *
     * @returns Listener handle exposing remove().
     */
    const studyDeletedEventListener = emitter.addListener(
      EMITTER_CONSTANTS.STUDY_DELETED,
      handleStudyDeletedSocket,
    );
    /**
     * The emitter subscription that forwards report-updated socket events to the report handler. Registered on the REPORT_UPDATED channel and removed when the effect cleans up.
     *
     * @returns Listener handle exposing remove().
     */
    const reportListener = emitter.addListener(
      EMITTER_CONSTANTS.REPORT_UPDATED,
      handleReportUpdatedSocket,
    );
    const inboxListener = emitter.addListener(EMITTER_CONSTANTS.INBOX_EVENTS, handleInboxSocket);
    /**
     * The emitter subscription that forwards mark-status-report socket events to the report status handler. Registered on the MARK_STATUS_REPORT channel and removed when the effect cleans up.
     *
     * @returns Listener handle exposing remove().
     */
    const markStatusReportEventListener = emitter.addListener(
      EMITTER_CONSTANTS.MARK_STATUS_REPORT,
      handleMarkStatusReportSocket,
    );
    return () => {
      deviceStatusListener.remove();
      studyUpdatedListener.remove();
      studyEventListener.remove();
      studyDeletedEventListener.remove();
      reportListener.remove();
      inboxListener.remove();
      markStatusReportEventListener.remove();
    };
  }, [state.studyDetails, state.isOpenDisclaimerModal]);

  useEffect(() => {
    if (state.isLoadedData) {
      if (locationState?.shouldLoadVerifiedReport) {
        hasNavigatedFromInboxPage.current = true;
        navigate(location.pathname, {
          state: {
            ...locationState,
            shouldLoadVerifiedReport: false,
          },
        });
        openVerifiedReportTab();
      } else if (
        locationState?.shouldViewInstantReportTab ||
        locationState?.shouldShoulViewReportTab
      ) {
        navigate(location.pathname, {
          state: {
            ...locationState,
            shouldViewInstantReportTab: false,
            shouldShoulViewReportTab: false,
          },
        });
      }
    }
  }, [state.isLoadedData]);

  useUpdateEffect(() => {
    preventAppScroll(true);
    setState(getInitialState());
    fetchData();
  }, [studyId]);

  useUpdateEffect(() => {
    if (state.isLoadedData) {
      preventAppScroll(false);
    }
  }, [state.isLoadedData]);

  useEmitter(EMITTER_CONSTANTS.DEVICE_STARTED_STUDY, handleDeviceStartedStudySocket, [
    state.studyDetails,
  ]);

  useEmitter(EMITTER_CONSTANTS.IS_SHOW_LOGIN_BY_BANNER, handleLoginBannerDisplayChange, []);

  return {
    studyId,
    location,
    locationState,
    navigate,
    state,
    setState,
    commentBoxVisible,
    setCommentBoxVisible,
    instantReportTabRef,
    headerRef,
    isWebView,
    isAbortingStudy,
    isC1cProviderStudy,
    isAbortedStudy,
    isArchivedStudy,
    hasStoppingStudyCommand,
    onClickBack,
    navigateTab,
    toggleDisplayPatientInfoDrawer,
    toggleECGViewer,
    toggleInboxECGViewer,
    updateStudyDetail,
    updateStudyData,
    handleUpdateReports,
    onClickOkayDisclaimerModal,
    updateReturnStatus,
    updateSentReferenceCodeStatus,
    handleAbortStudy,
    handleViewVerifiedReportClick,
    resetShouldLoadVerifiedReport,
    handleUpdateStudyAfterAssigningDevice,
    handleUpdateReferringPhysicians,
    handleRefreshTracking,
  };
};
